// Setup on a venue's settings landing (spec/13 §13): one card saying how far the venue's setup is and the way into it,
// in place of the old checklist that lived in this browser alone. The steps themselves are the server's
// (`GET …/venues/{v}/setup`), so this card and the Setup page can never disagree. Shown while the venue sets up, or has
// a step left; a venue that has opened with nothing left does not need it.

import { useTranslation } from '@clavian/i18n/react';
import { ProgressBar } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Section } from '../ui';
import { useVenueSetup } from './queries';

import './setup.css';

export function SetupSummary({ organizationId, venueId, venueSlug }: { readonly organizationId: string; readonly venueId: string; readonly venueSlug: string }): ReactNode {
  const { t } = useTranslation('console');
  const setup = useVenueSetup(organizationId, venueId);
  const data = setup.data;
  // Unreadable (a role without settings:read sees no settings either) or not yet read: nothing, rather than a placeholder.
  if (data === undefined) return null;
  const { progress } = data;
  if (data.status !== 'setup' && progress.left === 0) return null;
  return (
    <Section
      title={t('setup.summary.title')}
      description={t('setup.summary.intro')}
      actions={
        <Link className="clav-button clav-button--secondary clav-button--sm" to="/v/$venueSlug/setup" params={{ venueSlug }}>
          {t('setup.summary.open')}
        </Link>
      }
    >
      <div className="console-setup-summary__body">
        <ProgressBar value={progress.essentialsTotal === 0 ? 1 : progress.essentialsDone / progress.essentialsTotal} label={t('setup.progressLabel')} tone="ok" size="sm" />
        <p className="console-setup-summary__text">
          {progress.left === 0
            ? t('setup.summary.allDone')
            : t('setup.progress', {
                essentialsDone: progress.essentialsDone,
                essentialsTotal: progress.essentialsTotal,
                suggestedSettled: progress.suggestedSettled,
                suggestedTotal: progress.suggestedTotal,
              })}
        </p>
      </div>
    </Section>
  );
}
